import { useEffect, useRef } from 'react';
import { useAppConfig } from '@/api/useAppConfig';

/**
 * The classification banner in the middle of the header.
 *
 * Driven entirely by `Application:Branding:ConfidentialNotice`, and disabled in
 * this deployment's appsettings — it exists so the banner can be switched on per
 * environment without a rebuild, which is why the text and classes come from the
 * server rather than being hard-coded here.
 *
 * `Position` is part of the configuration but the header never read it: the
 * banner is always absolutely positioned in the header bar. That is left as-is
 * rather than "fixed", so behaviour does not change under the migration.
 */
export function ConfidentialNotice() {
  const { data: config } = useAppConfig();
  const notice = config?.confidentialNotice;
  const containerRef = useRef<HTMLDivElement>(null);

  // CustomCss is a raw declaration list from configuration ("background: red;
  // color: #fff"). Setting the attribute verbatim avoids parsing it into a React
  // style object, where a mistranslated property would silently drop a rule.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    if (notice?.customCss) {
      element.setAttribute('style', notice.customCss);
    } else {
      element.removeAttribute('style');
    }
  }, [notice?.customCss]);

  if (!notice?.enabled) return null;

  const visibility = notice.hideOnMobile ? 'd-none d-md-flex' : 'd-flex';

  return (
    <div
      ref={containerRef}
      className={`confidential-notice position-absolute start-50 translate-middle-x ${visibility}`}
    >
      <span className={`badge ${notice.cssClass} fs-sm fw-bold py-2 px-3 border`}>{notice.text}</span>
    </div>
  );
}
